import { LoadingState } from '../ui';
import { LastChanceChips } from '../ui';
// src/pages/SelectTeam.jsx

import React from "react";
import { useState, useEffect, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import SidebarNavbar from "../components/SidebarNavbar";
import BottomNavbar from "../components/BottomNavbar";
import { useNavigate, Link } from "react-router-dom";
import notify from '../lib/notify';
import LmsTeamSelection from '../components/selection/LmsTeamSelection';
import TeamChoice from '../components/selection/TeamChoice';
import {buildTeamCrestMap, teamAssetKey} from '../lib/teamAssets';
import { Button as FcButton, Card as FcCard, Input as FcInput } from '../ui';
import { Dialog as FcDialog } from '../ui';
import {EntrySelector} from '../ui';

export default function SelectTeam({ session, userRole, username }) {
  const navigate = useNavigate();
  // Logout handler
  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/login");
  };
  const { competitionId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  // Multi-entry: which of the user's entries in this competition we're playing.
  const [entryId, setEntryId] = useState(null);
  const [entries, setEntries] = useState([]); // [{ id, entry_index, entry_label, status }]
  const [entryStatus, setEntryStatus] = useState('active');
  const [addingEntry, setAddingEntry] = useState(false);
  const [fixtures, setFixtures] = useState([]);
  const [teamCrests, setTeamCrests] = useState({});
  const [eligibleTeams, setEligibleTeams] = useState([]); // array of team names
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [competition, setCompetition] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null); // holds { fixtureId, prediction, teamName }
  // Last Chance Chips state
  const [chipsRemaining, setChipsRemaining] = useState(0);
  const [chipBalance, setChipBalance] = useState(null);
  const [usingChip, setUsingChip] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  // Double-pick state (for abandoned/postponed fixtures)
  const [mustPickDouble, setMustPickDouble] = useState(false);
  const [abandonedOnMatchdays, setAbandonedOnMatchdays] = useState([]);
  const [firstSelection, setFirstSelection] = useState(null);
  const [secondSelection, setSecondSelection] = useState(null);
  const [existingSelections, setExistingSelections] = useState([]);
  const [replaceModal, setReplaceModal] = useState(null); // { newSelection, existingSelections }
  // Season postponement credit state
  const [postponementCredits, setPostponementCredits] = useState(0);
  const [redeemModal, setRedeemModal] = useState(null); // { newSelection }
  const [removeModal, setRemoveModal] = useState(null); // { selection }
  // Presentation state only; the established save queries and reload stay below.
  const [selectionDeadline, setSelectionDeadline] = useState(null);
  const [selectionFeedback, setSelectionFeedback] = useState(null);
  const [savingSelection, setSavingSelection] = useState(false);
  const savingSelectionRef = useRef(false);
  const reportNotice = (message, type = 'info') => {
    notify(message, type);
    if (competition?.type === 'knockout') setSelectionFeedback({ message, type });
  };

  // Drafts and late-chip consent belong to one entry, just like persisted picks.
  // Season retains its existing local-state behaviour.
  useEffect(() => {
    if (competition?.type !== 'knockout') return;
    setFirstSelection(null);
    setSecondSelection(null);
    setConfirmModal(null);
    setReplaceModal(null);
    setUsingChip(false);
    setSelectionFeedback(null);
  }, [entryId, competition?.type]);

  useEffect(() => {
    const fetchCompetitionAndFixtures = async () => {
      setLoading(true);
      setChipBalance(null);

      // 1) Get the signed-in user
      const {
        data: { user },
        error: userError,
      } = await supabase.auth.getUser();

      if (userError || !user) {
        setLoading(false);
        return;
      }
      // 2) Fetch competition details (including current_match_day)
      const { data: compData, error: compError } = await supabase
        .from("competitions")
        .select("id, name, league, season, current_match_day, type, is_multi_league, registration_status, status")
        .eq("id", competitionId)
        .single();
      if (compError || !compData) {
        setLoading(false);
        return;
      }
      setCompetition(compData);

      // Multi-league: the current round's playable pool is the union of the
      // fixtures for this round's mapped (league, season, match_day) entries.
      let roundMap = null;      // [{ league, season, match_day }]
      let mlLeagues = [];
      let mlMatchDays = [];
      let mlPairSet = new Set(); // "league|season|match_day"
      if (compData.is_multi_league) {
        const { data: mapRows } = await supabase
          .from("competition_round_matchdays")
          .select("league, season, match_day")
          .eq("competition_id", competitionId)
          .eq("round_number", compData.current_match_day);
        roundMap = mapRows || [];
        mlLeagues = [...new Set(roundMap.map((m) => m.league))];
        mlMatchDays = [...new Set(roundMap.map((m) => m.match_day))];
        mlPairSet = new Set(roundMap.map((m) => `${m.league}|${m.season}|${m.match_day}`));
      }

      // Multi-entry: load this user's entries for the competition, then decide
      // which one we're playing (from ?entry=, else the first by entry_index).
      const { data: entryRows } = await supabase
        .from('user_selections')
        .select('id, entry_index, entry_label, status')
        .eq('user_id', user.id)
        .eq('competition_id', competitionId)
        .order('entry_index', { ascending: true });

      const userEntries = entryRows || [];
      setEntries(userEntries);

      if (userEntries.length === 0) {
        // Not a member of this competition — nothing to select.
        setEntryId(null);
        setLoading(false);
        return;
      }

      const requestedEntry = searchParams.get('entry');
      const resolvedEntry =
        userEntries.find((e) => e.id === requestedEntry) || userEntries[0];
      const resolvedEntryId = resolvedEntry.id;
      setEntryId(resolvedEntryId);
      setEntryStatus(resolvedEntry.status || 'active');

      // Fetch this ENTRY's chip count, must_pick_double flag, postponement_credits, abandoned_on_matchdays
      const { data: userSelData } = await supabase
        .from('user_selections')
        .select('last_chance_chips_remaining, must_pick_double, postponement_credits, abandoned_on_matchdays')
        .eq('id', resolvedEntryId)
        .single();

      if (userSelData) {
        setChipBalance(Number.isSafeInteger(userSelData.last_chance_chips_remaining) && userSelData.last_chance_chips_remaining >= 0 ? userSelData.last_chance_chips_remaining : null);
        setChipsRemaining(userSelData.last_chance_chips_remaining || 0);
        setMustPickDouble(userSelData.must_pick_double || false);
        setPostponementCredits(userSelData.postponement_credits || 0);
        setAbandonedOnMatchdays(userSelData.abandoned_on_matchdays || []);
      }
      
      // Check if selection window is locked (1 hour before the first kickoff).
      // Multi-league: the first kickoff is the earliest across the round's mapped matchdays.
      let firstKickoffTime = null;
      if (compData.is_multi_league) {
        if (mlLeagues.length > 0) {
          const { data: mdRows } = await supabase
            .from('matchdays_view')
            .select('first_kickoff_time, league, season, match_day')
            .in('league', mlLeagues)
            .in('match_day', mlMatchDays);
          const times = (mdRows || [])
            .filter((r) => mlPairSet.has(`${r.league}|${r.season}|${r.match_day}`))
            .map((r) => r.first_kickoff_time)
            .filter(Boolean)
            .map((t) => new Date(t).getTime());
          if (times.length) firstKickoffTime = new Date(Math.min(...times));
        }
      } else {
        const { data: matchdayData } = await supabase
          .from('matchdays_view')
          .select('first_kickoff_time')
          .eq('league', compData.league)
          .eq('season', compData.season)
          .eq('match_day', compData.current_match_day)
          .single();
        if (matchdayData && matchdayData.first_kickoff_time) {
          firstKickoffTime = new Date(matchdayData.first_kickoff_time);
        }
      }

      if (firstKickoffTime) {
        const lockTime = new Date(firstKickoffTime.getTime() - 60 * 60 * 1000);
        setSelectionDeadline(lockTime);
        setIsLocked(new Date() > lockTime);
      } else {
        setSelectionDeadline(null);
      }
      
      // 3) In parallel, fetch fixtures for the round + eligible teams + current selection.
      //    Multi-league: fetch across the round's mapped leagues/matchdays (filtered
      //    to the exact pairs afterwards); single-league: the one league/matchday.
      const fixturesQuery = compData.is_multi_league
        ? supabase
            .from("fixtures")
            .select("*")
            .in("league", mlLeagues.length ? mlLeagues : ["__none__"])
            .in("match_day", mlMatchDays.length ? mlMatchDays : [-1])
            .order("kickoff_time", { ascending: true })
        : supabase
            .from("fixtures")
            .select("*")
            .eq("league", compData.league)
            .eq("season", compData.season)
            .eq("match_day", compData.current_match_day)
            .order("kickoff_time", { ascending: true });

      const [fixturesResult, eligibleResult, selectionResult, mediaResult] = await Promise.all([
        fixturesQuery,

        // Call the RPC to get eligible teams for THIS ENTRY
        supabase.rpc("get_eligible_teams_for_entry", {
          p_entry_id: resolvedEntryId,
        }),
        // Fetch current selections for this entry/matchday (may be multiple for double-pick)
        supabase
          .from("team_selections")
          .select("selected_team, fixture_id, prediction, selection_number")
          .eq("entry_id", resolvedEntryId)
          .eq("match_day", compData.current_match_day)
          .order("selection_number", { ascending: true }),
        // Optional existing artwork must never change gameplay or fail its core reads.
        Promise.resolve(supabase.from('teams').select('official_name, display_name, footystats_name, logo_url').not('logo_url', 'is', null))
          .catch(()=>({data:[], error:true})),
      ]);
      setTeamCrests(buildTeamCrestMap(mediaResult.error ? [] : mediaResult.data || []));

      // 4) Handle fixtures response
      if (fixturesResult.error) {
        setFixtures([]);
      } else {
        let fixturesData = fixturesResult.data || [];
        if (compData.is_multi_league) {
          // Keep only fixtures matching the round's exact (league, season, match_day) pairs.
          fixturesData = fixturesData.filter((f) => mlPairSet.has(`${f.league}|${f.season}|${f.match_day}`));
        }
        setFixtures(fixturesData);
      }

      // 5) Handle eligible teams response
      if (eligibleResult.error) {
        setEligibleTeams([]);
      } else {
        // RPC returns an array of objects like [{ team: "Team Name" }, ...]
        // Map to just the string names
        setEligibleTeams((eligibleResult.data || []).map((row) => row.team));
      }

      // 6) Handle current selections (may be multiple for double-pick)
      if (selectionResult && selectionResult.data && selectionResult.data.length > 0) {
        setExistingSelections(selectionResult.data);
        if (selectionResult.data.length === 1) {
          setSelectedTeam(selectionResult.data[0].selected_team);
        } else if (selectionResult.data.length >= 2) {
          // Multiple selections - show as comma-separated list
          setSelectedTeam(selectionResult.data.map(s => s.selected_team).join(', '));
        }
      } else {
        setSelectedTeam(null);
        setExistingSelections([]);
      }
      setLoading(false);
    };

    if (competitionId) {
      fetchCompetitionAndFixtures();
    }
  }, [competitionId, searchParams.get('entry')]);

  // Switch which entry we're viewing/playing (reloads via the ?entry param).
  const handleSwitchEntry = (newEntryId) => {
    if (!newEntryId || newEntryId === entryId) return;
    const next = new URLSearchParams(searchParams);
    next.set('entry', newEntryId);
    setSearchParams(next);
  };

  // Create another entry in this competition and switch to it.
  const handleAddEntry = async () => {
    if (addingEntry) return;
    // Registration must be open to add an entry (the RPC enforces this too).
    if (competition?.status !== 'active' || competition?.registration_status !== 'open') {
      notify('This competition is not open for new entries.', 'info');
      return;
    }
    setAddingEntry(true);
    try {
      const { data, error } = await supabase.rpc('add_competition_entry', {
        p_competition_id: competitionId,
        p_entry_label: null,
      });
      if (error) {
        notify(error.message || 'Failed to add entry.', 'error');
        return;
      }
      const newEntryId = data?.entry_id;
      notify(`New entry created (entry ${data?.entry_index || ''}).`, 'success');
      if (newEntryId) {
        const next = new URLSearchParams(searchParams);
        next.set('entry', newEntryId);
        setSearchParams(next);
      }
    } catch (err) {
      notify('Something went wrong adding an entry.', 'error');
    } finally {
      setAddingEntry(false);
    }
  };

  const confirmSelection = (fixtureId, prediction, teamName) => {
    if (competition?.type === 'knockout') setSelectionFeedback(null);
    if (entryStatus === 'pending' || entryStatus === 'rejected') {
      reportNotice(
        entryStatus === 'pending'
          ? 'This entry is awaiting approval. You can select a team once an organiser approves it.'
          : 'This entry was not approved for the competition.',
        'info'
      );
      return;
    }
    if (entryStatus === 'eliminated') {
      reportNotice('This entry has been eliminated and can no longer make selections.', 'info');
      return;
    }
    // Special case: If mustPickDouble is set AND current matchday is in abandoned_on_matchdays,
    // they're making an alternative selection in the SAME matchday after abandonment (clears flag)
    // Allow single selection in this case. Otherwise, force double-pick mode.
    const isAlternativeSelection = mustPickDouble && abandonedOnMatchdays.includes(competition.current_match_day) && competition.type === 'knockout';
    
    if (mustPickDouble && !isAlternativeSelection) {
      // Double-pick mode: need to select 2 teams (for future matchdays)
      if (!firstSelection) {
        setFirstSelection({ fixtureId, prediction, teamName });
        reportNotice(`First team selected: ${teamName}. Now select your second team (both must win!)`, 'info');
      } else if (firstSelection && !secondSelection) {
        // Prevent selecting same team twice
        if (firstSelection.teamName === teamName) {
          reportNotice('You must select a different team for your second pick', 'error');
          return;
        }
        setSecondSelection({ fixtureId, prediction, teamName });
        setConfirmModal({ 
          isDoublePick: true,
          firstSelection,
          secondSelection: { fixtureId, prediction, teamName }
        });
      }
    } else {
      // Standard single selection (OR alternative selection after abandonment)
      
      // For Season competitions with postponement credit and 1 existing selection
      if (competition.type === 'season' && existingSelections.length === 1 && postponementCredits > 0) {
        // Show redeem modal: "Replace or Redeem?"
        setRedeemModal({
          newSelection: { fixtureId, prediction, teamName },
          existingSelection: existingSelections[0]
        });
        return;
      }
      
      // Check if user already has 2 selections (max reached)
      if (existingSelections.length >= 2) {
        // Show replace modal
        setReplaceModal({
          newSelection: { fixtureId, prediction, teamName },
          existingSelections
        });
      } else if (existingSelections.length === 1) {
        // Has one selection but no credit - just replace
        setConfirmModal({ fixtureId, prediction, teamName, isReplace: true, replacingTeam: existingSelections[0].selected_team });
      } else {
        // No existing selection - standard confirm
        setConfirmModal({ fixtureId, prediction, teamName });
      }
    }
  };

  const handleConfirm = async () => {
    // Bypass lock check if user has must_pick_double (abandoned fixture - not their fault)
    const shouldBypassLock = mustPickDouble && competition.type === 'knockout';
    
    // Check if locked and chip not enabled (unless bypassing for abandoned fixture)
    if (isLocked && !usingChip && !shouldBypassLock) {
      reportNotice("Selection window is closed. Enable Last Chance Chip to proceed.", 'error');
      return;
    }
    
    if (isLocked && usingChip && chipsRemaining === 0) {
      reportNotice("You have no Last Chance Chips remaining.", 'error');
      return;
    }

    // 1) Get current user again (should be signed in)
    const {
      data: { user },
      error: userError,
    } = await supabase.auth.getUser();

    if (userError || !user) {
      return;
    }

    // Handle double-pick submission
    if (confirmModal.isDoublePick) {
      const { firstSelection: first, secondSelection: second } = confirmModal;
      
      // Build mutations for both selections with selection_number
      const mutations = [
        {
          user_id: user.id,
          competition_id: competitionId,
          entry_id: entryId,
          match_day: competition.current_match_day,
          fixture_id: first.fixtureId,
          selected_team: first.teamName,
          prediction: first.prediction,
          used_last_chance_chip: usingChip,
          selection_number: 1,
        },
        {
          user_id: user.id,
          competition_id: competitionId,
          entry_id: entryId,
          match_day: competition.current_match_day,
          fixture_id: second.fixtureId,
          selected_team: second.teamName,
          prediction: second.prediction,
          used_last_chance_chip: usingChip,
          selection_number: 2,
        }
      ];

      // Delete any existing selections for THIS ENTRY this matchday first
      await supabase
        .from("team_selections")
        .delete()
        .eq("entry_id", entryId)
        .eq("match_day", competition.current_match_day);

      // Insert both new selections
      const { error: insertError } = await supabase
        .from("team_selections")
        .insert(mutations);

      if (insertError) {
        reportNotice("Failed to save double-pick selections.", 'error');
        return;
      }

      // Reset the must_pick_double flag ONLY if making alternative selection on same matchday as abandonment
      // If fulfilling requirement on future matchday, backend will reset after verifying both are correct
      // Use the state we fetched at page load (abandonedOnMatchdays) since the trigger will clear it after INSERT
      if (mustPickDouble && abandonedOnMatchdays.includes(competition.current_match_day)) {
        // Making alternative double-pick on SAME matchday as abandonment - clear flag immediately
        const { error: resetError } = await supabase
          .from('user_selections')
          .update({ must_pick_double: false })
          .eq('id', entryId);

        if (resetError) {
        }
      }
      // Otherwise, flag stays true and backend will clear after both picks are verified correct

      // If chip was used, decrement the count
      if (usingChip) {
        await supabase
          .from('user_selections')
          .update({ last_chance_chips_remaining: chipsRemaining - 1 })
          .eq('id', entryId);
      }

      reportNotice(`Double-pick saved! ${first.teamName} and ${second.teamName} - BOTH must win!`, 'success');
      setConfirmModal(null);
      window.location.reload();
      return;
    }

    // Handle standard single selection
    const { fixtureId, prediction, teamName } = confirmModal;

    // 2) Find the chosen fixture (so we can grab its ID and other fields)
    const fixture = fixtures.find((f) => f.id === fixtureId);
    if (!fixture) {
      return;
    }

    // 3) Check if a selection for this user + competition + match_day already exists
    const { data: existingData, error: fetchError } = await supabase
      .from("team_selections")
      .select("id")
      .eq("entry_id", entryId)
      .eq("match_day", competition.current_match_day)
      .single(); // assume one pick per match day

    if (fetchError && fetchError.code !== "PGRST116") {
      return;
    }

    // 4) Build the mutation object
    const mutation = {
      user_id: user.id,
      competition_id: competitionId,
      entry_id: entryId,
      match_day: competition.current_match_day,
      fixture_id: fixture.id,
      selected_team: teamName,
      prediction: prediction, // either "home_win" or "away_win"
      used_last_chance_chip: usingChip, // Mark if chip was used
    };

    // 5) Insert or update accordingly
    let result;
    if (existingData && existingData.id) {
      result = await supabase
        .from("team_selections")
        .update(mutation)
        .eq("id", existingData.id);
    } else {
      result = await supabase.from("team_selections").insert([mutation]);
    }

    if (result.error) {
      reportNotice("Failed to save team selection.", 'error');
    } else {
      // Update user_selections flags
      const updates = {};
      
      // If chip was used, decrement the count
      if (usingChip) {
        updates.last_chance_chips_remaining = chipsRemaining - 1;
      }
      
      // If user has must_pick_double flag and makes a selection in the SAME matchday as the abandonment,
      // reset the flag since they've handled it by making an alternative selection
      // Use the state we fetched at page load (abandonedOnMatchdays) since the trigger will clear it after INSERT
      if (mustPickDouble && competition.type === 'knockout') {
        if (abandonedOnMatchdays.includes(competition.current_match_day)) {
          // Making alternative selection on SAME matchday as abandonment - clear flag
          updates.must_pick_double = false;
        }
        // Otherwise, keep flag true - user must make 2 correct picks on future matchday
      }
      
      // NOTE: postponement_credits should ONLY be consumed in handleRedeemCredit when explicitly redeemed
      // Do NOT reset it here - let users keep their credit for future matchdays
      
      if (Object.keys(updates).length > 0) {
        const { error: updateError } = await supabase
          .from('user_selections')
          .update(updates)
          .eq('id', entryId);

        if (updateError) {
        }
      }
      
      // Show appropriate success message
      if (mustPickDouble && competition.type === 'knockout') {
        // Check if flag was cleared or not
        if (updates.must_pick_double === false) {
          reportNotice(`Team selection saved! Alternative selection made - double-pick requirement cleared.`, 'success');
        } else {
          reportNotice(`Team selection saved! Remember: You must make 2 CORRECT picks this matchday to clear the double-pick requirement.`, 'success');
        }
      } else {
        reportNotice("Team selection saved successfully!", 'success');
      }
      
      setConfirmModal(null);
      // Refresh the page to update all UI state
      window.location.reload();
    }
  };

  const handleRedeemCredit = async () => {
    if (!redeemModal) return;
    
    const { newSelection } = redeemModal;
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    // Prevent selecting same team twice
    if (existingSelections[0].selected_team === newSelection.teamName) {
      notify('You cannot select the same team twice!', 'error');
      return;
    }

    // Insert second selection with selection_number = 2
    const mutation = {
      user_id: user.id,
      competition_id: competitionId,
      entry_id: entryId,
      match_day: competition.current_match_day,
      fixture_id: newSelection.fixtureId,
      selected_team: newSelection.teamName,
      prediction: newSelection.prediction,
      selection_number: 2,
      used_last_chance_chip: false,
    };

    const { error: insertError } = await supabase
      .from('team_selections')
      .insert([mutation]);

    if (insertError) {
      notify('Failed to redeem postponement credit.', 'error');
      return;
    }

    // Consume the credit
    const { error: updateError } = await supabase
      .from('user_selections')
      .update({ postponement_credits: 0 })
      .eq('id', entryId);

    if (updateError) {
    }

    notify(`Postponement credit redeemed! You now have 2 teams selected.`, 'success');
    setRedeemModal(null);
    window.location.reload();
  };

  const handleReplaceWithNew = async () => {
    if (!redeemModal) return;
    
    const { newSelection, existingSelection } = redeemModal;
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    // Replace existing selection
    const { error } = await supabase
      .from('team_selections')
      .update({
        fixture_id: newSelection.fixtureId,
        selected_team: newSelection.teamName,
        prediction: newSelection.prediction,
      })
      .eq('entry_id', entryId)
      .eq('match_day', competition.current_match_day)
      .eq('selection_number', 1);

    if (error) {
      notify('Failed to update selection.', 'error');
    } else {
      notify(`Replaced ${existingSelection.selected_team} with ${newSelection.teamName}`, 'success');
      setRedeemModal(null);
      window.location.reload();
    }
  };

  const handleRemoveSelection = async () => {
    if (!removeModal) return;
    
    const { selection } = removeModal;
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    // Delete the selection
    const { error } = await supabase
      .from('team_selections')
      .delete()
      .eq('entry_id', entryId)
      .eq('match_day', competition.current_match_day)
      .eq('selection_number', selection.selection_number);

    if (error) {
      notify('Failed to remove selection.', 'error');
    } else {
      // If they had 2 selections and deleted one, renumber the remaining to selection_number = 1
      if (existingSelections.length === 2) {
        const remainingSelection = existingSelections.find(s => s.selection_number !== selection.selection_number);
        if (remainingSelection) {
          await supabase
            .from('team_selections')
            .update({ selection_number: 1 })
            .eq('entry_id', entryId)
            .eq('match_day', competition.current_match_day)
            .eq('selection_number', remainingSelection.selection_number);
        }

        // Refund postponement credit if they had used it (Season competitions only)
        if (competition.type === 'season' && postponementCredits === 0) {
          await supabase
            .from('user_selections')
            .update({ postponement_credits: 1 })
            .eq('id', entryId);
          
          notify(`${selection.selected_team} removed. Postponement credit refunded!`, 'success');
        } else {
          notify(`${selection.selected_team} removed from your selections.`, 'success');
        }
      } else {
        notify(`${selection.selected_team} removed from your selections.`, 'success');
      }
      
      setRemoveModal(null);
      window.location.reload();
    }
  };

  const handleReplaceSelection = async (sel) => {
    // Check if new team is already selected in the OTHER slot
    const otherSelection = replaceModal.existingSelections.find(
      s => s.selection_number !== sel.selection_number
    );
    if (otherSelection && otherSelection.selected_team === replaceModal.newSelection.teamName) {
      reportNotice('You cannot select the same team twice!', 'error');
      return;
    }

    // Replace this selection
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    // Update the existing selection with new team
    const { error } = await supabase
      .from('team_selections')
      .update({
        fixture_id: replaceModal.newSelection.fixtureId,
        selected_team: replaceModal.newSelection.teamName,
        prediction: replaceModal.newSelection.prediction,
      })
      .eq('entry_id', entryId)
      .eq('match_day', competition.current_match_day)
      .eq('selection_number', sel.selection_number);

    if (error) {
      reportNotice('Failed to update selection.', 'error');
    } else {
      reportNotice(`Replaced ${sel.selected_team} with ${replaceModal.newSelection.teamName}`, 'success');
      setReplaceModal(null);
      window.location.reload();
    }
  };

  // Busy feedback wraps existing handlers; cancellation never invokes a write.
  const runLmsSave = async (action) => {
    if (savingSelectionRef.current) return;
    savingSelectionRef.current = true;
    setSavingSelection(true);
    setSelectionFeedback(null);
    try {
      await action();
    } catch (error) {
      reportNotice('Failed to save team selection.', 'error');
    } finally {
      savingSelectionRef.current = false;
      setSavingSelection(false);
    }
  };

  const cancelLmsSelection = () => {
    if (savingSelectionRef.current) return;
    setConfirmModal(null);
    setReplaceModal(null);
    setSelectionFeedback(null);
    if (confirmModal?.isDoublePick) {
      setFirstSelection(null);
      setSecondSelection(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-surface-canvas">
        {/* Responsive green banner for mobile */}
        <div className="block md:hidden w-full bg-ui-green py-3 px-4">
          <span className="text-white text-lg font-semibold">Footy Coach</span>
        </div>
        <SidebarNavbar handleLogout={handleLogout} userRole={userRole} />
        <BottomNavbar handleLogout={handleLogout} userRole={userRole} />
        <div role="main" id="fc-main-content" className="md:ml-64 pt-8 px-2 sm:px-4 md:p-8 fc-workspace">
          <LoadingState label="Loading..." />
        </div>
      </div>
    );
  }

  // Group fixtures by date (filter to future only if using chip, and always exclude abandoned/postponed)
  const displayFixtures = fixtures.filter(f => {
    // Always exclude abandoned and postponed fixtures
    if (f.status === 'abandoned' || f.status === 'postponed') return false;
    // If using chip while locked, only show future fixtures
    if (isLocked && usingChip) return f.kickoff_time && new Date(f.kickoff_time) > new Date();
    return true;
  });
    
  const fixturesByDate = displayFixtures.reduce((acc, fixture) => {
    const date = fixture.kickoff_time ? new Date(fixture.kickoff_time).toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : 'Unknown Date';
    if (!acc[date]) acc[date] = [];
    acc[date].push(fixture);
    return acc;
  }, {});

  if (competition?.type === 'knockout') {
    return <div className="min-h-screen bg-surface-canvas">
      <div className="block md:hidden w-full bg-ui-green py-3 px-4">
        <span className="text-white text-lg font-semibold">Footy Coach</span>
      </div>
      <SidebarNavbar handleLogout={handleLogout} userRole={userRole} />
      <BottomNavbar handleLogout={handleLogout} userRole={userRole} />
      <LmsTeamSelection {...{
        competition, competitionId, entries, entryId, entryStatus, username, eligibleTeams, teamCrests,
        selectedTeam, selectionDeadline, isLocked, chipsRemaining, chipBalance, usingChip, setUsingChip,
        mustPickDouble, abandonedOnMatchdays, firstSelection, existingSelections, fixturesByDate,
        confirmModal, replaceModal, addingEntry,
      }} feedback={selectionFeedback} saving={savingSelection}
        onSwitchEntry={handleSwitchEntry} onAddEntry={handleAddEntry} onChoose={confirmSelection}
        onConfirm={() => runLmsSave(handleConfirm)}
        onReplace={selection => runLmsSave(() => handleReplaceSelection(selection))}
        onCancel={cancelLmsSelection} />
    </div>;
  }

  return (
    <div className="min-h-screen bg-surface-canvas">
      {/* Responsive green banner for mobile */}
      <div className="block md:hidden w-full bg-ui-green py-3 px-4">
        <span className="text-white text-lg font-semibold">Footy Coach</span>
      </div>
      <SidebarNavbar handleLogout={handleLogout} userRole={userRole} />
      <BottomNavbar handleLogout={handleLogout} userRole={userRole} />
      <div role="main" id="fc-main-content" className="md:ml-64 pt-8 px-2 sm:px-4 md:p-8 pb-28 md:pb-8 fc-workspace">
        <h2 className="text-2xl font-bold mb-2 text-brand-text">Team Selection - {competition?.name || ''}</h2>

        {/* Entry awaiting approval / rejected banner */}
        {(entryStatus === 'pending' || entryStatus === 'rejected') && (
          <div className={`mb-4 p-3 rounded-lg border ${entryStatus === 'pending' ? 'bg-amber-50 border-amber-300 text-amber-800' : "bg-red-50 border-red-300 text-ui-error"}`}>
            <div className="font-semibold">
              {entryStatus === 'pending' ? '⏳ Awaiting approval' : 'Entry not approved'}
            </div>
            <div className="text-sm mt-1">
              {entryStatus === 'pending'
                ? 'An organiser needs to approve this entry before you can select a team.'
                : 'This entry was not approved for the competition.'}
            </div>
          </div>
        )}

        {/* Eliminated entry banner */}
        {entryStatus === 'eliminated' && (
          <div className="mb-4 p-3 rounded-lg border bg-red-50 border-red-300 text-ui-error">
            <div className="font-semibold">Entry eliminated</div>
            <div className="text-sm mt-1">
              This entry has been eliminated and can no longer make selections.
            </div>
          </div>
        )}

        {/* Multi-entry bar: switch between your entries / add another */}
        {entries.length > 0 && (competition?.type === 'season' || competition?.type === 'knockout') && (
          <FcCard as="div" padding="standard" className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 bg-surface-card border border-ui-border rounded-lg p-3">
                <EntrySelector className="flex-1" aria-label="Your entry"
                  value={entryId || ''}
                  onChange={(e) => handleSwitchEntry(e.target.value)}
                  readOnlyValue={entries.length>1?undefined:(entries[0].entry_label || `${username || 'Entry'} ${entries[0].entry_index}`)}
                >
                  {entries.map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.entry_label || `${username || 'Entry'} ${e.entry_index}`}
                    </option>
                  ))}
                </EntrySelector>
            {/* Only allow adding entries while registration is open (matches CompetitionView
                and the server-side add_competition_entry RPC). */}
            {competition?.status === 'active' && competition?.registration_status === 'open' && (
              <FcButton variant="secondary" size="compact"
                type="button"
                onClick={handleAddEntry}
                disabled={addingEntry}
                className="border disabled:cursor-not-allowed"
              >
                {addingEntry ? 'Adding…' : '+ Add another entry'}
              </FcButton>
            )}
          </FcCard>
        )}


        {/* Last Chance Chip Banner - Only show if no selection made yet AND not bypassing for abandonment */}
        {isLocked && !selectedTeam && !mustPickDouble && (
          chipsRemaining > 0 ? (
            <div className="mb-4 p-3 rounded-lg bg-action-primary bg-opacity-10 border border-ui-border">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <div className="font-semibold text-action-primary">⏰ Selection Window Closed</div>
                  <div className="text-sm text-content-primary mt-1">You can use a Last Chance Chip to make a late selection</div>
                </div>
                <div className="text-center sm:text-right">
                  <LastChanceChips count={chipBalance} compact />
                </div>
              </div>
              <label className="flex items-center mt-3 cursor-pointer">
                <FcInput aria-label="Use Last Chance Chip for this selection"
                  type="checkbox"
                  checked={usingChip}
                  onChange={(e) => setUsingChip(e.target.checked)}
                  className="mr-2 w-4"
                />
                <span className="text-sm font-medium">Use Last Chance Chip for this selection</span>
              </label>
            </div>
          ) : (
            <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-300">
              <div className="font-semibold text-ui-error">Selection Window Closed</div>
              <div className="text-sm text-ui-error mt-1">You have no Last Chance Chips remaining</div>
            </div>
          )
        )}
        
        {/* Show selection closed message if user already has a selection */}
        {isLocked && selectedTeam && !mustPickDouble && (
          <div className="mb-4 p-3 rounded-lg bg-gray-100 border border-ui-border">
            <div className="font-semibold text-content-primary">Selection Window Closed</div>
            <div className="text-sm text-content-secondary mt-1">You have already made your selection for this matchday</div>
          </div>
        )}
        
        {/* Double-Pick Requirement Banner */}
        {mustPickDouble && existingSelections.length < 2 && (
          <div className="mb-4 p-4 bg-red-50 border-2 border-red-400 rounded-lg">
            <div className="flex items-start gap-3">
              <span className="text-3xl">⚠️</span>
              <div className="flex-1">
                <div className="font-bold text-ui-error text-lg">Double-Pick Required</div>
                <div className="text-sm text-ui-error mt-1">
                  Your fixture was abandoned/postponed with no alternatives available. 
                  You <strong>MUST select 2 teams</strong> for this matchday - <strong>BOTH must win</strong> for you to survive!
                </div>
                {firstSelection && (
                  <div className="mt-2 p-2 bg-red-100 rounded">
                    <span className="font-semibold text-ui-error">1st Pick: {firstSelection.teamName}</span>
                    <span className="text-ui-error text-sm ml-2">✓ Now select your 2nd team</span>
                  </div>
                )}
                {!firstSelection && (
                  <div className="mt-2 text-ui-error font-medium">
                    Select your first team below ↓
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-4">
          <div className="flex flex-col">
            <div className="text-brand-text-muted font-semibold text-base">
              Current Match Day: <span className="text-brand-text font-bold">{competition?.current_match_day}</span>
            </div>
          </div>
          <div className="mt-3 sm:mt-0">
            <Link to={`/competition/${competitionId}`} className="text-action-primary font-semibold text-sm hover:underline">
              View Competition
            </Link>
          </div>
        </div>

        {/* Current Selections Display with Remove Buttons (Season only) */}
        {existingSelections.length > 0 && competition.type === 'season' && (
          <div className="mb-4 bg-surface-canvas border border-blue-300 rounded-lg p-4">
            <div className="font-semibold text-ui-green mb-2">Your Current Selection{existingSelections.length > 1 ? 's' : ''}:</div>
            <div className="space-y-2">
              {existingSelections.map((sel) => (
                <FcCard as="div" padding="standard" key={sel.selection_number} className="flex items-center justify-between bg-surface-card rounded p-3 border border-blue-200">
                  <div className="font-semibold text-content-primary">{sel.selection_number}. {sel.selected_team}</div>
                  <FcButton variant="destructive-secondary" size="compact" type="submit"
                    className=""
                    onClick={() => setRemoveModal({ selection: sel })}
                  >
                    Remove
                  </FcButton>
                </FcCard>
              ))}
            </div>
            {postponementCredits > 0 && existingSelections.length === 1 && (
              <div className="mt-3 text-sm text-ui-green">
                💡 You have a postponement credit! Click any team below to add a 2nd selection.
              </div>
            )}
          </div>
        )}

        {/* Knockout Current Selections Display */}
        {existingSelections.length > 0 && competition.type !== 'season' && (
          <div className="mb-4">
            <div className="text-brand-text-muted font-semibold text-base">
              Current Selection{mustPickDouble && existingSelections.length > 1 ? 's' : ''}: 
              <span className="text-brand-accent-from font-bold">{selectedTeam}</span>
              {mustPickDouble && existingSelections.length === 1 && (
                <span className="text-ui-error text-sm ml-2">(Need 1 more!)</span>
              )}
              {mustPickDouble && existingSelections.length >= 2 && (
                <span className="text-ui-success text-sm ml-2">✓ Both selected</span>
              )}
            </div>
          </div>
        )}

        {/* Fixtures grouped by date */}
        {Object.keys(fixturesByDate).length === 0 ? (
          <div className="w-full bg-gray-100 text-content-secondary rounded-lg p-6 text-center text-base font-medium">
            No fixtures available for this match day.
          </div>
        ) : (
          Object.entries(fixturesByDate).map(([date, dayFixtures]) => (
            <div key={date} className="mb-6">
              <div className="font-bold text-lg text-brand-text mb-2 text-center w-full">{date}</div>
              <div className="fc-season-fixtures">
                {dayFixtures.map((fixture) => {
                  const kickoff = fixture.kickoff_time ? new Date(fixture.kickoff_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';
                  const homeSelectable = eligibleTeams.includes(fixture.home_team) && selectedTeam !== fixture.home_team;
                  const awaySelectable = eligibleTeams.includes(fixture.away_team) && selectedTeam !== fixture.away_team;
                  return (
                    <FcCard as="div" padding="standard"
                      key={fixture.id}
                      className="bg-surface-card rounded-lg shadow p-3 border border-ui-border fc-season-fixture"
                    >
                      {/* Home team */}
                      <div className="min-w-0">
                        <TeamChoice type="submit" name={fixture.home_team} side="home" crest={teamCrests[teamAssetKey(fixture.home_team)]}
                          disabled={!homeSelectable}
                          onClick={() => confirmSelection(fixture.id, 'home_win', fixture.home_team)}
                        />
                      </div>
                      {/* Kickoff time */}
                      <div className="fc-season-fixture__kickoff">
                        <span className="text-brand-text-muted text-lg">{kickoff}</span>
                      </div>
                      {/* Away team */}
                      <div className="min-w-0">
                        <TeamChoice type="submit" name={fixture.away_team} side="away" crest={teamCrests[teamAssetKey(fixture.away_team)]}
                          disabled={!awaySelectable}
                          onClick={() => confirmSelection(fixture.id, 'away_win', fixture.away_team)}
                        />
                      </div>
                    </FcCard>
                  );
                })}
              </div>
            </div>
          ))
        )}

        {/* Confirmation modal */}
        {confirmModal && (
          <FcDialog open title={confirmModal.isDoublePick ? "Confirm Double-Pick" : "Confirm Your Selection"} size="standard" onCancel={() => {
                    setConfirmModal(null);
                    if (confirmModal.isDoublePick) {
                      // Reset selections if canceling double-pick
                      setFirstSelection(null);
                      setSecondSelection(null);
                    }
                  }}>
            <FcCard as="div" padding="standard" className="bg-surface-card p-6 rounded-lg shadow-xl text-center max-w-md mx-auto">

              {confirmModal.isDoublePick ? (
                <div className="mb-6">
                  <p className="text-content-primary mb-3">
                    You've selected <strong className="text-content-primary">2 teams</strong>:
                  </p>
                  <div className="bg-red-50 border border-red-300 rounded p-3 mb-3">
                    <div className="font-semibold text-ui-error">1st Pick: {confirmModal.firstSelection.teamName}</div>
                    <div className="font-semibold text-ui-error mt-1">2nd Pick: {confirmModal.secondSelection.teamName}</div>
                  </div>
                  <p className="text-sm text-ui-error font-medium">
                    ⚠️ BOTH teams must win for you to survive!
                  </p>
                </div>
              ) : (
                <p className="mb-6 text-content-primary">
                  Are you sure you want to select{' '}
                  <strong className="text-content-primary">{confirmModal.teamName}</strong>?
                </p>
              )}
              <div className="flex justify-center gap-4">
                <FcButton variant="primary" type="submit"
                  className=""
                  onClick={handleConfirm}
                >
                  {confirmModal.isDoublePick ? 'Confirm Both Picks' : 'Yes'}
                </FcButton>
                <FcButton variant="tertiary" type="submit"
                  className=""
                  onClick={() => {
                    setConfirmModal(null);
                    if (confirmModal.isDoublePick) {
                      // Reset selections if canceling double-pick
                      setFirstSelection(null);
                      setSecondSelection(null);
                    }
                  }}
                >
                  Cancel
                </FcButton>
              </div>
            </FcCard>
          </FcDialog>
        )}

        {/* Replace selection modal */}
        {replaceModal && (
          <FcDialog open title="Replace Existing Selection" size="standard" onCancel={() => setReplaceModal(null)}>
            <FcCard as="div" padding="standard" className="bg-surface-card p-6 rounded-lg shadow-xl text-center max-w-md mx-auto">

              <p className="mb-4 text-content-primary">
                You want to select <strong className="text-content-primary">{replaceModal.newSelection.teamName}</strong>.
              </p>
              <p className="mb-4 text-content-primary">
                Which existing team do you want to replace?
              </p>
              <div className="space-y-3 mb-6">
                {replaceModal.existingSelections.map((sel) => (
                  <FcButton variant="secondary" type="submit"
                    key={sel.fixture_id}
                    className="w-full border border-orange-300"
                    onClick={() => handleReplaceSelection(sel)}
                  >
                    Replace: <strong>{sel.selected_team}</strong>
                  </FcButton>
                ))}
              </div>
              <FcButton variant="tertiary" type="submit"
                className=""
                onClick={() => setReplaceModal(null)}
              >
                Cancel
              </FcButton>
            </FcCard>
          </FcDialog>
        )}

        {/* Redeem Postponement Credit Modal (Season competitions) */}
        {redeemModal && (
          <FcDialog open title="Use Postponement Credit?" size="standard" onCancel={() => setRedeemModal(null)}>
            <FcCard as="div" padding="standard" className="bg-surface-card p-6 rounded-lg shadow-xl text-center max-w-md mx-auto">

              <p className="mb-4 text-content-primary">
                You want to select <strong className="text-content-primary">{redeemModal.newSelection.teamName}</strong>.
              </p>
              <p className="mb-4 text-content-primary">
                You currently have <strong className="text-content-primary">{redeemModal.existingSelection.selected_team}</strong> selected.
              </p>
              <p className="mb-6 text-content-primary font-semibold">
                Do you want to:
              </p>
              <div className="space-y-3 mb-4">
                <FcButton variant="primary" type="submit"
                  className="w-full"
                  onClick={handleRedeemCredit}
                >
                  🎫 Redeem Credit (Keep Both Teams)
                </FcButton>
                <FcButton variant="tertiary" type="submit"
                  className="w-full"
                  onClick={handleReplaceWithNew}
                >
                  Replace {redeemModal.existingSelection.selected_team}
                </FcButton>
              </div>
              <FcButton variant="tertiary" type="submit"
                className=""
                onClick={() => setRedeemModal(null)}
              >
                Cancel
              </FcButton>
            </FcCard>
          </FcDialog>
        )}

        {/* Remove Selection Confirmation Modal */}
        {removeModal && (
          <FcDialog open title="⚠️ Remove Selection?" size="standard" onCancel={() => setRemoveModal(null)}>
            <FcCard as="div" padding="standard" className="bg-surface-card p-6 rounded-lg shadow-xl text-center max-w-md mx-auto">

              <p className="mb-6 text-content-primary">
                Are you sure you want to remove{' '}
                <strong className="text-content-primary">{removeModal.selection.selected_team}</strong> from your selections?
              </p>
              {existingSelections.length === 2 && postponementCredits === 0 && (
                <p className="mb-6 text-sm text-orange-600 bg-orange-50 p-3 rounded">
                  💡 Tip: You can save this double-selection for a future matchday if you prefer!
                </p>
              )}
              <div className="flex justify-center gap-4">
                <FcButton variant="destructive" type="submit"
                  className=""
                  onClick={handleRemoveSelection}
                >
                  Yes, Remove
                </FcButton>
                <FcButton variant="tertiary" type="submit"
                  className=""
                  onClick={() => setRemoveModal(null)}
                >
                  Cancel
                </FcButton>
              </div>
            </FcCard>
          </FcDialog>
        )}
      </div>
    </div>
  );
}
